import { apiClient } from './api-client';

// ==================== Types ====================

export interface DashboardWidget {
  id: string;
  tenantId: string;
  dashboardId: string;
  widgetType: string;
  title: string;
  config: Record<string, any>;
  position: { x: number; y: number; w: number; h: number };
  drillDownConfig?: Record<string, any>;
  refreshInterval?: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardConfig {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  roleType: string;
  isDefault: boolean;
  isOrgDefault: boolean;
  layout: { widgetId: string; x: number; y: number; w: number; h: number }[];
  settings: Record<string, any>;
  widgets: DashboardWidget[];
  createdAt: string;
  updatedAt: string;
}

export interface WidgetCatalogItem {
  type: string;
  name: string;
  description: string;
  category: string;
  defaultSize: { w: number; h: number };
  minSize?: { w: number; h: number };
  maxSize?: { w: number; h: number };
  availableFor: string[];
  configSchema?: Record<string, any>;
}

export interface DashboardAnnotation {
  id: string;
  dashboardId: string;
  widgetId?: string;
  dataPoint?: Record<string, any>;
  content: string;
  createdBy: string;
  createdAt: string;
}

// ==================== Custom Metrics Types ====================

export interface CustomMetricDefinition {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  key: string;
  category?: string;
  /** Reports tab this metric is grouped under; blank/null = default "Custom Metrics" tab. */
  tabName?: string | null;
  dataSource: string;
  metricType: string;
  querySpec: Record<string, any>;
  chartType?: string;
  displayFormat?: string;
  thresholds: Record<string, number>;
  unit?: string;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MetricEvaluationResult {
  type: string;
  value?: number;
  metrics?: { label: string; value: number }[];
  total?: number;
  successRate?: number;
  error?: string;
}

// ==================== Dashboard API ====================

class DashboardsApiService {
  async listDashboards(): Promise<DashboardConfig[]> {
    const { data } = await apiClient.get('/dashboards');
    return data;
  }

  async getDashboard(id: string): Promise<DashboardConfig> {
    const { data } = await apiClient.get(`/dashboards/${id}`);
    return data;
  }

  async createDashboard(dto: { name: string; roleType?: string; settings?: Record<string, any> }): Promise<DashboardConfig> {
    const { data } = await apiClient.post('/dashboards', dto);
    return data;
  }

  async updateDashboard(id: string, dto: { name?: string; layout?: any[]; settings?: Record<string, any> }): Promise<DashboardConfig> {
    const { data } = await apiClient.put(`/dashboards/${id}`, dto);
    return data;
  }

  async deleteDashboard(id: string): Promise<void> {
    await apiClient.delete(`/dashboards/${id}`);
  }

  // Widgets
  async addWidget(dashboardId: string, dto: {
    widgetType: string;
    title: string;
    config?: Record<string, any>;
    position?: { x: number; y: number; w: number; h: number };
  }): Promise<DashboardWidget> {
    const { data } = await apiClient.post(`/dashboards/${dashboardId}/widgets`, dto);
    return data;
  }

  async updateWidget(dashboardId: string, widgetId: string, dto: {
    title?: string;
    config?: Record<string, any>;
    position?: { x: number; y: number; w: number; h: number };
    isVisible?: boolean;
  }): Promise<DashboardWidget> {
    const { data } = await apiClient.put(`/dashboards/${dashboardId}/widgets/${widgetId}`, dto);
    return data;
  }

  async removeWidget(dashboardId: string, widgetId: string): Promise<void> {
    await apiClient.delete(`/dashboards/${dashboardId}/widgets/${widgetId}`);
  }

  // Catalog & Defaults
  async getWidgetCatalog(): Promise<WidgetCatalogItem[]> {
    const { data } = await apiClient.get('/dashboards/widgets/catalog');
    return data;
  }

  async seedDefault(): Promise<DashboardConfig> {
    const { data } = await apiClient.post('/dashboards/seed-default');
    return data;
  }

  async setOrgDefault(dashboardId: string, roleType: string): Promise<DashboardConfig> {
    const { data } = await apiClient.put(`/dashboards/${dashboardId}/set-org-default`, { roleType });
    return data;
  }

  // Annotations
  async addAnnotation(dashboardId: string, dto: { widgetId?: string; dataPoint?: Record<string, any>; content: string }): Promise<DashboardAnnotation> {
    const { data } = await apiClient.post(`/dashboards/${dashboardId}/annotations`, dto);
    return data;
  }

  async listAnnotations(dashboardId: string): Promise<DashboardAnnotation[]> {
    const { data } = await apiClient.get(`/dashboards/${dashboardId}/annotations`);
    return data;
  }
}

// ==================== Custom Metrics API ====================

class CustomMetricsApiService {
  async listMetrics(activeOnly = true): Promise<CustomMetricDefinition[]> {
    const { data } = await apiClient.get('/custom-metrics', { params: { activeOnly } });
    return data;
  }

  async getMetric(id: string): Promise<CustomMetricDefinition> {
    const { data } = await apiClient.get(`/custom-metrics/${id}`);
    return data;
  }

  async createMetric(dto: Omit<CustomMetricDefinition, 'id' | 'tenantId' | 'isActive' | 'createdBy' | 'createdAt' | 'updatedAt'>): Promise<CustomMetricDefinition> {
    const { data } = await apiClient.post('/custom-metrics', dto);
    return data;
  }

  async updateMetric(id: string, dto: Partial<CustomMetricDefinition>): Promise<CustomMetricDefinition> {
    const { data } = await apiClient.put(`/custom-metrics/${id}`, dto);
    return data;
  }

  async deleteMetric(id: string): Promise<void> {
    await apiClient.delete(`/custom-metrics/${id}`);
  }

  async evaluateMetric(id: string, startDate?: string, endDate?: string): Promise<MetricEvaluationResult> {
    const { data } = await apiClient.post(`/custom-metrics/${id}/evaluate`, { startDate, endDate });
    return data;
  }

  async getTimeSeries(id: string, startDate?: string, endDate?: string, interval = 'day'): Promise<any[]> {
    const { data } = await apiClient.get(`/custom-metrics/${id}/time-series`, {
      params: { startDate, endDate, interval },
    });
    return data;
  }

  async previewMetric(dto: { dataSource: string; metricType: string; querySpec: Record<string, any>; startDate?: string; endDate?: string }): Promise<MetricEvaluationResult> {
    const { data } = await apiClient.post('/custom-metrics/preview', dto);
    return data;
  }

  /**
   * Generate querySpec from natural language using AI.
   */
  async generateQuerySpec(dto: {
    naturalLanguage: string;
    dataSource: string;
    metricType: string;
  }): Promise<{
    success: boolean;
    querySpec?: Record<string, any>;
    explanation?: string;
    confidence?: number;
    warnings?: string[];
    error?: string;
  }> {
    const { data } = await apiClient.post('/custom-metrics/generate-query-spec', dto);
    // Transform snake_case response from Python API to camelCase
    return {
      success: data.success,
      querySpec: data.query_spec,
      explanation: data.explanation,
      confidence: data.confidence,
      warnings: data.warnings,
      error: data.error,
    };
  }
}

// ==================== Org Dashboard Template Types ====================

export interface OrgDashboardWidget {
  id: string;
  tenantId: string;
  templateId: string;
  widgetType: string;
  title: string;
  config: Record<string, any>;
  position: { x: number; y: number; w: number; h: number };
  isMandatory: boolean;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrgDashboardTemplate {
  id: string;
  tenantId: string;
  roleType: string;
  name: string;
  layout: { widgetId: string; x: number; y: number; w: number; h: number }[];
  settings: Record<string, any>;
  widgets: OrgDashboardWidget[];
  createdAt: string;
  updatedAt: string;
}

export interface OrgWidgetCatalogItem extends WidgetCatalogItem {
  isMandatory: boolean;
  chartTypes?: string[];
  defaultChartType?: string;
  recommendedSize?: { w: number; h: number };
}

// ==================== Org Dashboard Templates API ====================

class OrgDashboardTemplatesApiService {
  async getMyDashboard(): Promise<OrgDashboardTemplate> {
    const { data } = await apiClient.get('/org-dashboard-templates/my-dashboard');
    return data;
  }

  async getTemplate(roleType: string): Promise<OrgDashboardTemplate> {
    const { data } = await apiClient.get(`/org-dashboard-templates/${roleType}`);
    return data;
  }

  async updateTemplate(roleType: string, dto: { layout?: any[]; settings?: Record<string, any> }): Promise<OrgDashboardTemplate> {
    const { data } = await apiClient.put(`/org-dashboard-templates/${roleType}`, dto);
    return data;
  }

  async addWidget(roleType: string, dto: {
    widgetType: string;
    title: string;
    config?: Record<string, any>;
    position?: { x: number; y: number; w: number; h: number };
  }): Promise<OrgDashboardWidget> {
    const { data } = await apiClient.post(`/org-dashboard-templates/${roleType}/widgets`, dto);
    return data;
  }

  async updateWidget(roleType: string, widgetId: string, dto: {
    title?: string;
    config?: Record<string, any>;
    position?: { x: number; y: number; w: number; h: number };
    isEnabled?: boolean;
  }): Promise<OrgDashboardWidget> {
    const { data } = await apiClient.put(`/org-dashboard-templates/${roleType}/widgets/${widgetId}`, dto);
    return data;
  }

  async removeWidget(roleType: string, widgetId: string): Promise<void> {
    await apiClient.delete(`/org-dashboard-templates/${roleType}/widgets/${widgetId}`);
  }

  async getCatalog(roleType: string): Promise<OrgWidgetCatalogItem[]> {
    const { data } = await apiClient.get(`/org-dashboard-templates/catalog/${roleType}`);
    return data;
  }
}

// ==================== Singleton Exports ====================

export const dashboardsService = new DashboardsApiService();
export const customMetricsService = new CustomMetricsApiService();
export const orgDashboardTemplatesService = new OrgDashboardTemplatesApiService();
